import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button component styled per TinyChapters brand guide:
 * - Rounded corners: 8-12px
 * - Primary fill: Primary Peach
 * - Text: Soft Ink
 * - Hover: slightly darker shade
 */

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // Primary - Peach with dark text (brand default)
        default:
          'bg-primary text-foreground hover:brightness-95 active:brightness-90 shadow-brand hover:shadow-brand-hover',

        // Destructive - Blush (softer than harsh red)
        destructive:
          'bg-destructive text-foreground hover:brightness-95 active:brightness-90',

        // Outline - transparent with border
        outline:
          'border-2 border-primary bg-transparent text-foreground hover:bg-primary/10 active:bg-primary/20',

        // Secondary - Mint
        secondary:
          'bg-secondary text-foreground hover:brightness-95 active:brightness-90',

        // Ghost - transparent, subtle hover
        ghost:
          'hover:bg-muted hover:text-foreground',

        // Link - text only with underline
        link:
          'text-primary underline-offset-4 hover:underline',

        // Accent - Lavender
        accent:
          'bg-accent text-foreground hover:brightness-95 active:brightness-90',

        // Success - Mint (same as secondary but semantically different)
        success:
          'bg-success text-foreground hover:brightness-95 active:brightness-90',
      },
      size: {
        default: 'h-10 px-5 py-2 rounded-[10px]',
        sm: 'h-9 px-4 rounded-[8px] text-xs',
        lg: 'h-12 px-8 rounded-[12px] text-base',
        icon: 'h-10 w-10 rounded-[10px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
